import { supabase } from './supabase';
import { 
  FurnitureRemovalOperation, 
  CreateFurnitureRemovalOperationRequest, 
  UpdateFurnitureRemovalOperationRequest 
} from '../types/furniture-removal';

export const furnitureRemovalService = {
  // Create a new furniture removal operation
  async createOperation(
    userId: string,
    request: CreateFurnitureRemovalOperationRequest
  ): Promise<FurnitureRemovalOperation> {
    console.log('💾 Creating furniture removal operation:', request.processing_id);
    
    const { data, error } = await supabase
      .from('furniture_removal_operations')
      .insert({
        user_id: userId,
        processing_id: request.processing_id,
        operation_type: request.operation_type,
        clean_selected_prompt: request.clean_selected_prompt,
        original_image_url: request.original_image_url,
        tokens_spent: request.tokens_spent || 0,
        webhook_url: request.webhook_url,
        processing_status: 'processing'
      })
      .select()
      .single();

    if (error) {
      console.error('❌ Error creating furniture removal operation:', error);
      throw error;
    }

    console.log('✅ Furniture removal operation created:', data.id);
    return data;
  },

  // Update an existing furniture removal operation
  async updateOperation(
    processingId: string,
    updates: UpdateFurnitureRemovalOperationRequest
  ): Promise<FurnitureRemovalOperation> {
    console.log('🔄 Updating furniture removal operation:', processingId);
    
    const updateData: any = {
      ...updates,
      updated_at: new Date().toISOString()
    };

    if (updates.processing_status === 'completed') {
      updateData.completed_at = new Date().toISOString();
    }

    const { data, error } = await supabase
      .from('furniture_removal_operations')
      .update(updateData)
      .eq('processing_id', processingId)
      .select()
      .single();

    if (error) {
      console.error('❌ Error updating furniture removal operation:', error);
      throw error;
    }

    console.log('✅ Furniture removal operation updated:', data.id);
    return data;
  },

  // Get all operations for a user
  async getUserOperations(userId: string): Promise<FurnitureRemovalOperation[]> {
    const { data, error } = await supabase
      .from('furniture_removal_operations')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Error fetching user furniture removal operations:', error);
      throw error;
    }

    return data || [];
  },

  // Get a specific operation by processing ID
  async getOperationByProcessingId(processingId: string): Promise<FurnitureRemovalOperation | null> {
    const { data, error } = await supabase
      .from('furniture_removal_operations')
      .select('*')
      .eq('processing_id', processingId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') { // No rows returned
        return null;
      }
      console.error('❌ Error fetching furniture removal operation:', error);
      throw error;
    }

    return data;
  },

  // Mark operation as failed
  async markOperationFailed(
    processingId: string,
    errorMessage: string
  ): Promise<FurnitureRemovalOperation> {
    return this.updateOperation(processingId, {
      processing_status: 'failed',
      error_message: errorMessage,
      completed_at: new Date().toISOString()
    });
  },

  // Mark operation as completed with result
  async markOperationCompleted(
    processingId: string,
    resultImageUrl: string,
    n8nResponse?: any
  ): Promise<FurnitureRemovalOperation> {
    return this.updateOperation(processingId, {
      processing_status: 'completed',
      result_image_url: resultImageUrl,
      n8n_response: n8nResponse,
      completed_at: new Date().toISOString()
    });
  }
};

